/**
 * 模型配置页面
 */
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { toast, confirm } from '../store/toastStore';
import PageHeader from '../components/layout/PageHeader';

interface ModelConfig {
  id: number;
  model_name: string;
  provider: string;
  base_url: string;
  is_default: boolean;
  is_active?: boolean;
  max_concurrent_requests: number;
  request_delay: number;
  system_model_id?: number | null;
  system_model_name?: string | null;
  readonly?: boolean;
  auto_disabled?: boolean;
  display_name?: string | null;
  created_at: string;
}

const ModelConfigPage: React.FC = () => {
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [testResult, setTestResult] = useState<{ id: number; success: boolean; message: string; responseTime?: number } | null>(null);
  const [formData, setFormData] = useState({
    model_name: '',
    display_name: '',
    provider: 'openai',
    api_key: '',
    base_url: '',
    is_default: false,
    max_concurrent_requests: 3,
    request_delay: 0,
  });

  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/models');
      setModels(response.data.models || []);
    } catch (error) {
      console.error('加载模型配置失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/api/models/${editingId}`, formData);
      } else {
        await api.post('/api/models', formData);
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({
        model_name: '',
        display_name: '',
        provider: 'openai',
        api_key: '',
        base_url: '',
        is_default: false,
        max_concurrent_requests: 3,
        request_delay: 0,
      });
      loadModels();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || '保存失败');
    }
  };

  const handleEdit = (model: ModelConfig) => {
    if (model.readonly) {
      toast.warning('系统模型不可编辑，只能禁用或切换默认');
      return;
    }
    setEditingId(model.id);
    setFormData({
      model_name: model.model_name,
      display_name: model.display_name || '',
      provider: model.provider,
      api_key: '',
      base_url: model.base_url,
      is_default: model.is_default,
      max_concurrent_requests: model.max_concurrent_requests,
      request_delay: model.request_delay,
    });
    setShowForm(true);
  };

  const handleDelete = async (model: ModelConfig) => {
    if (model.readonly) {
      toast.warning('系统模型不可删除，只能禁用');
      return;
    }
    if (!await confirm({ title: '确认删除', message: '确定要删除这个模型配置吗？', confirmLabel: '删除', danger: true })) return;
    try {
      await api.delete(`/api/models/${model.id}`);
      loadModels();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || '删除失败');
    }
  };

  const handleSetDefault = async (id: number) => {
    try {
      await api.put(`/api/models/${id}/set-default`);
      loadModels();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || '设置默认失败');
    }
  };

  const handleTest = async (id: number) => {
    setTestingId(id);
    setTestResult(null);
    try {
      const response = await api.post(`/api/models/${id}/test`);
      setTestResult({
        id,
        success: response.data.success,
        message: response.data.message,
        responseTime: response.data.response_time
      });
    } catch (error: any) {
      setTestResult({
        id,
        success: false,
        message: error.response?.data?.detail || '测试失败'
      });
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-2">
      {/* 头部标题与操作 */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <PageHeader title="模型设置" />
          <p className="text-xs text-muted-foreground mt-1">配置及验证您的 LLM 服务商 API 密钥与请求限制</p>
        </div>
        {!showForm && (
          <button
            onClick={() => {
              setShowForm(true);
              setEditingId(null);
              setFormData({
                model_name: '',
                provider: 'openai',
                api_key: '',
                base_url: '',
                is_default: false,
                max_concurrent_requests: 3,
                request_delay: 0,
              });
            }}
            className="inline-flex items-center px-4 py-2.5 bg-brand-700 hover:bg-[#0042b3] text-white text-sm font-semibold rounded-xl transition-all duration-300 shadow-[0_4px_12px_rgba(0,82,217,0.15)] active:scale-[0.98] transform"
          >
            <svg className="w-4 h-4 mr-1.5 stroke-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            添加模型
          </button>
        )}
      </div>

      {showForm && (
        <div className="mb-8 bg-white rounded-2xl border border-border p-6 shadow-[0_12px_33px_0_rgba(0,0,0,0.015),0_3.6px_9.9px_0_rgba(0,0,0,0.01)] animate-fade-in">
          <h2 className="text-base font-bold text-foreground mb-6">
            {editingId ? '⚡ 编辑模型配置' : '✨ 添加模型配置'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-2">
                  模型名称
                </label>
                <input
                  type="text"
                  value={formData.model_name}
                  onChange={(e) => setFormData({ ...formData, model_name: e.target.value })}
                  placeholder="例如: gpt-4o, deepseek-chat"
                  className="w-full px-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:border-[#0052d9] focus:ring-1 focus:ring-[#0052d9]/40 transition-all placeholder-gray-400"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-2">
                  自定义名称 <span className="text-muted-foreground font-normal">(可选，留空则用模型名称)</span>
                </label>
                <input type="text" value={formData.display_name} onChange={e=>setFormData({...formData,display_name:e.target.value})}
                  placeholder="例如: 我的推理模型"
                  className="w-full px-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:border-[#0052d9] focus:ring-1 focus:ring-[#0052d9]/40 transition-all placeholder-gray-400" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-2">
                  服务提供商
                </label>
                <select
                  value={formData.provider}
                  onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                  className="w-full px-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:border-[#0052d9] focus:ring-1 focus:ring-[#0052d9]/40 appearance-none bg-white transition-all"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.25em 1.25em'
                  }}
                >
                  <option value="openai">OpenAI (兼容 API)</option>
                  <option value="azure">Azure OpenAI</option>
                  <option value="custom">自定义 (Custom)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-2">
                  API Key
                </label>
                <input
                  type="password"
                  value={formData.api_key}
                  onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                  placeholder={editingId ? '留空则不修改原始 Key' : '输入您的 API 密钥'}
                  className="w-full px-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:border-[#0052d9] focus:ring-1 focus:ring-[#0052d9]/40 transition-all placeholder-gray-400"
                  required={!editingId}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-2">
                  Base URL (接口接入地址 - 可选)
                </label>
                <input
                  type="text"
                  value={formData.base_url}
                  onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                  placeholder="例如: https://api.deepseek.com/v1"
                  className="w-full px-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:border-[#0052d9] focus:ring-1 focus:ring-[#0052d9]/40 transition-all placeholder-gray-400"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                  最大并发请求数
                </label>
                <input
                  type="number"
                  value={formData.max_concurrent_requests}
                  onChange={(e) => setFormData({ ...formData, max_concurrent_requests: parseInt(e.target.value) || 3 })}
                  min="1"
                  max="20"
                  className="w-full px-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:border-[#0052d9] focus:ring-1 focus:ring-[#0052d9]/40 transition-all"
                />
                <p className="text-[10px] text-muted-foreground mt-1">控制此模型同时向接口发送的并发数量</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                  请求延迟间隔 (毫秒)
                </label>
                <input
                  type="number"
                  value={formData.request_delay}
                  onChange={(e) => setFormData({ ...formData, request_delay: parseInt(e.target.value) || 0 })}
                  min="0"
                  max="10000"
                  className="w-full px-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:border-[#0052d9] focus:ring-1 focus:ring-[#0052d9]/40 transition-all"
                />
                <p className="text-[10px] text-muted-foreground mt-1">每次调用之间的强制缓冲期，防频限流</p>
              </div>
            </div>

            <div className="flex items-center pt-2">
              <input
                type="checkbox"
                id="is_default"
                checked={formData.is_default}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                className="h-4 w-4 text-primary focus:ring-[#0052d9]/40 border-border rounded-lg transition-all"
              />
              <label htmlFor="is_default" className="ml-2.5 text-xs font-semibold text-foreground select-none">
                将此项设为当前项目的默认 LLM 模型
              </label>
            </div>

            <div className="flex space-x-3 pt-4 border-t border-border/80">
              <button
                type="submit"
                className="px-5 py-2.5 bg-brand-700 hover:bg-[#0042b3] text-white text-xs font-bold rounded-xl shadow-sm transition-colors"
              >
                保存配置
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                }}
                className="px-5 py-2.5 bg-accent hover:bg-accent text-foreground text-xs font-semibold border border-border rounded-xl transition-colors"
              >
                取消
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052d9]"></div>
          <span className="ml-3 text-sm text-muted-foreground font-medium">读取模型数据中...</span>
        </div>
      ) : models.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-border shadow-[0_4px_16px_rgba(0,0,0,0.01)] px-4">
          <svg className="w-12 h-12 text-muted-foreground mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-muted-foreground font-medium mb-4">暂无配置的大模型服务</p>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center px-4 py-2 border border-border text-xs font-semibold rounded-xl text-primary bg-white hover:bg-accent transition-all shadow-sm"
          >
            配置您的第一个 LLM 服务
          </button>
        </div>
      ) : !showForm && (
        <div className="space-y-4">
          {models.map((model) => (
            <div
              key={model.id}
              className="bg-white rounded-2xl border border-border p-6 shadow-[0_12px_33px_0_rgba(0,0,0,0.015),0_3.6px_9.9px_0_rgba(0,0,0,0.01)] hover:border-[#0052d9]/10 hover:shadow-[0_15px_35px_rgba(0,0,0,0.03)] transition-all duration-300 flex flex-col md:flex-row justify-between items-start md:items-center gap-6"
            >
              <div className="flex-1">
                <div className="flex items-center space-x-2.5 mb-2">
                  <h3 className="text-base font-bold text-foreground">
                    {model.display_name || model.model_name}
                  </h3>
                  {model.display_name && model.display_name !== model.model_name && (
                    <span className="text-[11px] text-muted-foreground ml-2 truncate">{model.model_name}</span>
                  )}
                  <span className="px-2 py-0.5 text-[9px] font-semibold text-muted-foreground bg-accent border border-border/50 rounded-lg">
                    {model.provider.toUpperCase()}
                  </span>
                  {model.readonly && (
                    <span className="px-2 py-0.5 text-[9px] font-bold bg-blue-50 text-blue-700 border border-blue-200/80 rounded-lg ml-2">
                      系统配置
                    </span>
                  )}
                  {!model.readonly && model.system_model_id && (
                    <span className="px-2 py-0.5 text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200/80 rounded-lg ml-2">
                      用户配置
                    </span>
                  )}
                  {!model.readonly && model.system_model_name && (
                    <span className="px-2 py-0.5 text-[9px] text-muted-foreground bg-accent rounded-lg ml-2">
                      {model.system_model_name}
                    </span>
                  )}
                  {model.is_default && (
                    <span className="px-2.5 py-0.5 text-[9px] font-bold bg-primary/10 text-primary border border-[#dbeafe]/80 rounded-lg shadow-sm">
                      当前默认
                    </span>
                  )}
                  {!model.readonly && (
                    <button
                      onClick={async () => {
                        const res = await api.put(`/api/models/${model.id}`, { is_active: !model.is_active });
                        if (res.status === 200) {
                          setModels(prev => prev.map(m => m.id === model.id ? { ...m, is_active: res.data.is_active } : m));
                        }
                      }}
                      className={`px-2.5 py-0.5 text-[9px] font-bold rounded-lg border transition-all ${
                        model.is_active
                          ? 'bg-green-50 text-green-700 border-green-200/80 hover:bg-green-100'
                          : 'bg-accent text-muted-foreground border-border/80 hover:bg-accent'
                      }`}
                    >
                      {model.is_active ? '● 已启用' : '○ 已禁用'}
                    </button>
                  )}
                  {model.readonly && model.auto_disabled && (
                    <span className="px-2.5 py-0.5 text-[9px] font-bold rounded-lg border bg-red-50 text-red-700 border-red-200/80">
                      配额耗尽已禁用
                    </span>
                  )}
                  {model.readonly && !model.auto_disabled && (
                    <span className={`px-2.5 py-0.5 text-[9px] font-bold rounded-lg border ${
                      model.is_active
                        ? 'bg-green-50 text-green-700 border-green-200/80'
                        : 'bg-accent text-muted-foreground border-border/80'
                    }`}>
                      {model.is_active ? '● 已启用' : '○ 已禁用'}
                    </span>
                  )}
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 mt-3.5 text-xs text-muted-foreground">
                  {model.base_url && (
                    <div className="flex items-center truncate" title={model.base_url}>
                      <span className="font-semibold text-muted-foreground mr-1.5">接入端点:</span>
                      <span className="font-mono text-[11px] truncate">{model.base_url}</span>
                    </div>
                  )}
                  <div className="flex items-center">
                    <span className="font-semibold text-muted-foreground mr-1.5">并发控制:</span>
                    <span>最大 <span className="font-semibold text-foreground">{model.max_concurrent_requests}</span> 个并发请求</span>
                    {model.request_delay > 0 && <span className="ml-1 text-muted-foreground">| 间隔 {model.request_delay}ms</span>}
                  </div>
                  <div className="flex items-center">
                    <span className="font-semibold text-muted-foreground mr-1.5">配置时间:</span>
                    <span>{new Date(model.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                {testResult && testResult.id === model.id && (
                  <div className={`mt-4 p-3.5 rounded-xl text-xs flex items-start gap-2.5 ${
                    testResult.success 
                      ? 'bg-[#eafaf1] border border-[#d1f2e1]/80 text-[#18a058]' 
                      : 'bg-red-50 border border-red-200/50 text-red-800'
                  }`}>
                    <span className="text-sm">{testResult.success ? '✓' : '✗'}</span>
                    <div className="flex-1">
                      <p className="font-bold">
                        {testResult.success ? '连接测试通过' : '连接测试失败'}
                      </p>
                      <p className="mt-1.5 leading-relaxed text-muted-foreground">{testResult.message}</p>
                      {testResult.responseTime && (
                        <p className="mt-2 text-[10px] opacity-75 font-semibold">
                          平均请求响应时延: <span className="font-mono">{testResult.responseTime}s</span>
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex flex-row md:flex-col md:space-y-2.5 space-x-3 md:space-x-0 w-full md:w-auto pt-4 md:pt-0 border-t md:border-t-0 border-border/80 justify-end">
                <button
                  onClick={() => handleTest(model.id)}
                  disabled={testingId === model.id}
                  className={`text-xs font-bold px-3 py-2 rounded-xl transition-all ${
                    testingId === model.id
                      ? 'bg-accent text-muted-foreground cursor-not-allowed border border-border'
                      : 'text-primary bg-white border border-border hover:bg-primary/10/40 hover:border-[#0052d9]/20'
                  }`}
                >
                  {testingId === model.id ? '正在连接...' : '测试连接'}
                </button>
                {!model.is_default && (
                  <button
                    onClick={() => handleSetDefault(model.id)}
                    className="text-xs font-bold px-3 py-2 text-foreground bg-white border border-border hover:bg-accent rounded-xl transition-all"
                  >
                    设为默认
                  </button>
                )}
                {!model.readonly && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(model)}
                      className="text-xs font-bold px-3 py-2 rounded-xl transition-all text-[#18a058] bg-white border border-border hover:bg-[#eafaf1]/40"
                    >
                      编辑
                    </button>
                    <button
                      onClick={() => handleDelete(model)}
                      className="text-xs font-bold px-3 py-2 rounded-xl transition-all text-red-500 bg-white border border-border hover:bg-red-50"
                    >
                      删除
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModelConfigPage;
